import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ClassStudentSelect, TargetModeSelect } from "@/components/ClassStudentSelect";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colorOf, GRADE_GROUPS } from "@/lib/subjects";
import { Markdown } from "@/lib/markdown";
import { toast } from "sonner";
import {
  BookText, Plus, Trash2, Eye, EyeOff, Pencil, Printer, FileText, Sparkles, PenLine,
  Upload, Download, Loader2, HelpCircle, ChevronDown, ChevronRight, ClipboardCheck, Settings2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPES = { ozet: "Konu Özeti", ezber: "Ezber Kağıdı", quiz: "Quiz", deneme: "Deneme" };
const TABS = [
  { key: "manual", label: "Elle Yaz", icon: PenLine },
  { key: "ai", label: "Yapay Zeka", icon: Sparkles },
  { key: "file", label: "Dosya Yükle", icon: Upload },
];
const STATUS = {
  beklemede: { label: "Beklemede", badge: "bg-slate-100 text-slate-600 border-slate-200", bar: "border-l-slate-300" },
  ozet_bitti: { label: "Özet Bitti", badge: "bg-amber-100 text-amber-800 border-amber-300", bar: "border-l-amber-400" },
  ezber_gecti: { label: "Ezber Geçti", badge: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "border-l-emerald-500" },
};
const STATUS_STEPS = [
  { key: "beklemede", label: "Beklemede" },
  { key: "ozet_bitti", label: "Özet Bitti" },
  { key: "ezber_gecti", label: "Ezber Geçti" },
];
const fmtNet = (n) => Number((n ?? 0).toFixed(2)).toLocaleString("tr-TR");
const key = (s, u) => `${s}||${u}`;

export default function OzetEzber() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const isEditor = user?.role === "admin" || user?.role === "koc";

  const [subjects, setSubjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [myGrade, setMyGrade] = useState(null);
  const [contents, setContents] = useState([]);
  const [files, setFiles] = useState([]);
  const [expanded, setExpanded] = useState(new Set());

  // öğrenci bazlı ilerleme
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [mode, setMode] = useState(params.get("grade") ? "class" : "student");
  const [clsGrade, setClsGrade] = useState(Number(params.get("grade")) || 1);
  const [clsSection, setClsSection] = useState(params.get("section") || "");
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [students, setStudents] = useState([]);
  const [progress, setProgress] = useState([]);

  // admin içerik yönetimi
  const [manageOpen, setManageOpen] = useState(false);
  const [adminSubj, setAdminSubj] = useState("");
  const [newUnit, setNewUnit] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [tab, setTab] = useState("manual");
  const [form, setForm] = useState({ unit: "", type: "ozet", title: "", body: "", quiz: [] });
  const [aiKind, setAiKind] = useState("ozet");
  const [aiGrade, setAiGrade] = useState(4);
  const [aiBusy, setAiBusy] = useState(false);
  const [fileKind, setFileKind] = useState("ozet");
  const [fileUnit, setFileUnit] = useState("");
  const [fileBusy, setFileBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const [view, setView] = useState(null);
  const [examCtx, setExamCtx] = useState(null); // { subject, unit }
  const [examForm, setExamForm] = useState({ dogru: "", yanlis: "", bos: "", date: new Date().toISOString().slice(0, 10) });

  const studentGrade = useMemo(() => students.find((s) => s.id === studentId)?.grade ?? myGrade, [students, studentId, myGrade]);

  const loadAll = useCallback(() => {
    api.get("/subjects").then(({ data }) => { setSubjects(data); if (data[0]) setAdminSubj((p) => p || data[0].name); });
    api.get("/units", { params: studentGrade ? { grade: studentGrade } : {} }).then(({ data }) => setUnits(data));
    api.get("/contents").then(({ data }) => setContents(data));
    api.get("/content-files").then(({ data }) => setFiles(data));
  }, [studentGrade]);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (user?.role === "ogrenci" || user?.role === "veli") {
      api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); setMyGrade(data.grade ?? null); });
    } else if (isEditor) {
      api.get("/students").then(({ data }) => setStudents(data));
    }
  }, [user, isEditor]);

  useEffect(() => {
    if (isEditor && studentId) {
      const st = students.find((s) => s.id === studentId);
      if (st) setStudentName(st.name);
    }
  }, [isEditor, studentId, students]);

  const loadProgress = useCallback(() => {
    if (!studentId) { setProgress([]); return; }
    api.get("/unit-progress", { params: { student_id: studentId } }).then(({ data }) => setProgress(data));
  }, [studentId]);
  useEffect(() => { loadProgress(); }, [loadProgress]);

  const progressMap = useMemo(() => {
    const m = {};
    progress.forEach((p) => { m[key(p.subject_name, p.unit)] = p; });
    return m;
  }, [progress]);

  // ders -> ünite ağacı (units + içerik/dosyalardaki üniteleri birleştir)
  const tree = useMemo(() => {
    return subjects.map((s) => {
      const names = new Set();
      units.filter((u) => u.subject_name === s.name).forEach((u) => names.add(u.name));
      contents.filter((c) => c.subject_name === s.name).forEach((c) => names.add(c.unit || "Genel"));
      files.filter((f) => f.subject_name === s.name).forEach((f) => names.add(f.unit || "Genel"));
      const unitNames = [...names].sort((a, b) => a.localeCompare(b, "tr"));
      return { subject: s, unitNames };
    });
  }, [subjects, units, contents, files]);

  useEffect(() => { if (subjects.length && expanded.size === 0) setExpanded(new Set(subjects.map((s) => s.name))); }, [subjects]); // eslint-disable-line

  const toggleExpand = (name) => setExpanded((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });

  const adminUnits = useMemo(() => units.filter((u) => u.subject_name === adminSubj), [units, adminSubj]);

  const addUnit = async () => {
    if (!newUnit.trim()) return;
    try { await api.post("/units", { subject_name: adminSubj, name: newUnit.trim() }); setNewUnit(""); loadAll(); toast.success("Ünite eklendi"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkAddUnits = async () => {
    const names = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!names.length) return toast.error("Ünite adı girin (her satıra bir ünite)");
    try {
      for (const n of names) await api.post("/units", { subject_name: adminSubj, name: n });
      setBulkText(""); setBulkOpen(false); loadAll();
      toast.success(`${names.length} ünite eklendi ✓`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.body.trim()) return toast.error("İçerik boş olamaz");
    try {
      if (editing) { await api.put(`/contents/${editing}`, form); toast.success("Güncellendi ✓"); }
      else { await api.post("/contents", { subject_name: adminSubj, ...form, published: true }); toast.success("Kaydedildi ve yayınlandı ✓"); }
      setForm({ unit: "", type: "ozet", title: "", body: "", quiz: [] });
      setEditing(null);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate", { subject_name: adminSubj, unit: form.unit, kind: aiKind, grade: aiGrade });
      if (aiKind === "quiz" || aiKind === "deneme") {
        let parsed = null;
        try { parsed = JSON.parse(data.text.replace(/```json|```/g, "").trim()); } catch (e) {}
        if (parsed && Array.isArray(parsed)) {
          const md = ["## " + (form.unit || adminSubj) + " — " + TYPES[aiKind], ...parsed.flatMap((q, i) => [
            `\n**${i + 1}. ${q.question}**`, ...q.options.map((o) => `- ${o}`), `- ✅ Cevap: **${q.answer}**`,
          ])].join("\n");
          setForm((f) => ({ ...f, type: aiKind, body: md, quiz: parsed }));
        } else { setForm((f) => ({ ...f, type: aiKind, body: data.text, quiz: [] })); }
      } else { setForm((f) => ({ ...f, type: aiKind, body: data.text, quiz: [] })); }
      setTab("manual");
      toast.success("AI içeriği üretildi — kaydetmeyi unutmayın ✓");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI üretimi başarısız"); }
    finally { setAiBusy(false); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/content-files", fd, { params: { subject_name: adminSubj, unit: fileUnit, kind: fileKind } });
      toast.success("Dosya yüklendi ✓"); loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFileBusy(false); e.target.value = ""; }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/content-files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("İndirme başarısız"); }
  };
  const removeFile = async (id) => { try { await api.delete(`/content-files/${id}`); toast.success("Dosya silindi"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const startEdit = (c) => { setManageOpen(true); setAdminSubj(c.subject_name); setEditing(c.id); setForm({ unit: c.unit, type: c.type, title: c.title, body: c.body, quiz: c.quiz || [] }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const togglePublish = async (c) => { await api.put(`/contents/${c.id}`, { published: !c.published }); loadAll(); };
  const remove = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/contents/${id}`); loadAll(); toast.success("Silindi"); };

  const recordAttempt = async (res) => {
    if (user?.role !== "ogrenci") return;
    try { await api.post("/quiz-attempts", { content_id: view.id, title: view.title || view.unit || "Test", subject_name: view.subject_name, ...res }); toast.success("Sonuç quiz geçmişine kaydedildi ✓"); }
    catch (e) { toast.error("Sonuç kaydedilemedi"); }
  };

  const setStatus = async (subject, unit, status) => {
    if (mode === "class") {
      try {
        const { data } = await api.post("/unit-progress/assign-class", { grade: clsGrade, class_section: clsSection || null, subject_name: subject, unit, status });
        toast.success(`${data.count} öğrencinin "${unit}" durumu güncellendi ✓`);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    if (!studentId) return toast.error("Önce öğrenci seçin");
    try { await api.post("/unit-progress", { student_id: studentId, subject_name: subject, unit, status }); loadProgress(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openExam = (subject, unit) => {
    const ex = progressMap[key(subject, unit)]?.exam;
    setExamForm(ex ? { dogru: String(ex.dogru), yanlis: String(ex.yanlis), bos: String(ex.bos), date: ex.date } : { dogru: "", yanlis: "", bos: "", date: new Date().toISOString().slice(0, 10) });
    setExamCtx({ subject, unit });
  };
  const saveExam = async () => {
    try {
      await api.post("/unit-progress/exam", { student_id: studentId, subject_name: examCtx.subject, unit: examCtx.unit, dogru: Number(examForm.dogru) || 0, yanlis: Number(examForm.yanlis) || 0, bos: Number(examForm.bos) || 0, date: examForm.date });
      toast.success("Sınav sonucu kaydedildi ✓"); setExamCtx(null); loadProgress();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const clearExam = async (subject, unit) => {
    if (!window.confirm("Sınav sonucu silinsin mi?")) return;
    try { await api.delete("/unit-progress/exam", { params: { student_id: studentId, subject_name: subject, unit } }); loadProgress(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const getContents = (s, u) => contents.filter((c) => c.subject_name === s && (c.unit || "Genel") === u);
  const getFiles = (s, u) => files.filter((f) => f.subject_name === s && (f.unit || "Genel") === u);
  const allShownContents = useMemo(() => contents, [contents]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600"><BookText className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Özet &amp; Ezber</h1>
            <p className="text-slate-500">Ders → ünite; özet/ezber takibi, quiz ve ünite sınavları{studentName ? ` · ${studentName}` : ""}</p>
          </div>
        </div>
        <div className="flex items-end gap-2">
          {isAdmin && (
            <button data-testid="oe-manage-toggle" onClick={() => setManageOpen((o) => !o)} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
              <Settings2 className="h-5 w-5" /> İçerik Yönetimi
            </button>
          )}
          <button data-testid="oe-print-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 font-semibold text-slate-700 shadow-sm transition hover:shadow-md active:scale-95">
            <Printer className="h-5 w-5" /> Yazdır / PDF
          </button>
        </div>
      </div>

      {isEditor && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm no-print">
          <label className={lbl}>Hedef (özet/ezber takibi için)</label>
          <TargetModeSelect students={students} mode={mode} setMode={setMode} studentId={studentId} setStudentId={(v) => setStudentId(v || null)} grade={clsGrade} setGrade={setClsGrade} section={clsSection} setSection={setClsSection} testid="oe-student-select" />
        </div>
      )}

      {isAdmin && manageOpen && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 no-print">
          <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><Plus className="h-5 w-5" /> İçerik Ekle</h3>
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className={lbl}>Ders</label>
              <select data-testid="oe-admin-subject" value={adminSubj} onChange={(e) => setAdminSubj(e.target.value)} className={inp}>
                {subjects.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Ünite</label>
              <select data-testid="oe-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inp}>
                <option value="">Ünite seç</option>
                {adminUnits.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
              </select>
              <div className="mt-2 flex gap-2">
                <input data-testid="oe-new-unit" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="+ yeni ünite" className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-indigo-400" />
                <button data-testid="oe-add-unit-btn" onClick={addUnit} className="rounded-lg bg-slate-800 px-3 text-sm font-semibold text-white active:scale-95">Ekle</button>
                <button data-testid="oe-bulk-toggle" onClick={() => setBulkOpen((o) => !o)} className="rounded-lg border border-slate-300 px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 active:scale-95">Toplu</button>
              </div>
              {bulkOpen && (
                <div className="mt-2">
                  <textarea data-testid="oe-bulk-units" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={4} placeholder={"Her satıra bir ünite:\n1. Ünite\n2. Ünite\n3. Ünite"} className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-indigo-400" />
                  <button data-testid="oe-bulk-add-btn" onClick={bulkAddUnits} className="mt-1.5 w-full rounded-lg bg-indigo-600 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 active:scale-95">Hepsini Ekle</button>
                </div>
              )}
            </div>
            <div>
              <label className={lbl}>Tür</label>
              <select data-testid="oe-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp}>
                <option value="ozet">Konu Özeti</option>
                <option value="ezber">Ezber Kağıdı</option>
                <option value="quiz">Quiz</option>
                <option value="deneme">Deneme</option>
              </select>
            </div>
            <div>
              <label className={lbl}>Başlık (isteğe bağlı)</label>
              <input data-testid="oe-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Boş bırakılırsa ünite adı" className={inp} />
            </div>
          </div>

          <div className="mt-4 flex gap-2 border-b border-slate-200">
            {TABS.map((t) => { const Icon = t.icon; return (
              <button key={t.key} data-testid={`oe-tab-${t.key}`} onClick={() => setTab(t.key)} className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition ${tab === t.key ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            ); })}
          </div>

          {tab === "manual" && (
            <textarea data-testid="oe-form-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={7} placeholder="Markdown destekli: ## Başlık, - madde, **kalın**" className="mt-3 w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-sm outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100" />
          )}
          {tab === "ai" && (
            <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
              <p className="mb-3 text-sm text-slate-600">Ders, ünite ve sınıfı seçin; AI içerik üretsin.</p>
              <div className="flex flex-wrap items-center gap-3">
                <select data-testid="oe-ai-kind" value={aiKind} onChange={(e) => setAiKind(e.target.value)} className={inp}>
                  <option value="ozet">Konu Özeti Üret</option>
                  <option value="ezber">Ezber Kağıdı Üret</option>
                  <option value="quiz">Quiz Üret (5 soru)</option>
                  <option value="deneme">Deneme Üret (5 soru)</option>
                </select>
                <select data-testid="oe-ai-grade" value={aiGrade} onChange={(e) => setAiGrade(Number(e.target.value))} className={inp}>
                  {GRADE_GROUPS.map((grp) => <optgroup key={grp.label} label={grp.label}>{grp.grades.map((g) => <option key={g} value={g}>{g}. Sınıf</option>)}</optgroup>)}
                </select>
                <button data-testid="oe-ai-generate-btn" onClick={generateAI} disabled={aiBusy} className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95 disabled:opacity-60">
                  {aiBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />} {aiBusy ? "Üretiliyor…" : "AI ile Üret"}
                </button>
              </div>
            </div>
          )}
          {tab === "file" && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
              <p className="mb-3 text-sm text-slate-600">PDF veya fotoğraf yükleyin.</p>
              <div className="flex flex-wrap items-center gap-3">
                <select data-testid="oe-file-unit" value={fileUnit} onChange={(e) => setFileUnit(e.target.value)} className={inp}>
                  <option value="">Ünite (opsiyonel)</option>
                  {adminUnits.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
                <select data-testid="oe-file-kind" value={fileKind} onChange={(e) => setFileKind(e.target.value)} className={inp}>
                  <option value="ozet">Konu Özeti</option><option value="ezber">Ezber Kağıdı</option>
                  <option value="quiz">Quiz</option><option value="deneme">Deneme</option>
                </select>
                <label data-testid="oe-file-upload-label" className={`flex cursor-pointer items-center gap-2 rounded-xl bg-slate-800 px-5 py-2.5 font-bold text-white transition hover:bg-slate-900 active:scale-95 ${fileBusy ? "opacity-60" : ""}`}>
                  {fileBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />} PDF / Fotoğraf Seç
                  <input data-testid="oe-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={uploadFile} className="hidden" />
                </label>
              </div>
            </div>
          )}

          <div className="mt-3 flex gap-2">
            <button data-testid="oe-save-btn" onClick={save} className="flex items-center gap-2 rounded-xl bg-indigo-700 px-5 py-2.5 font-bold text-white transition hover:bg-indigo-800 active:scale-95">
              <Plus className="h-5 w-5" /> {editing ? "Güncelle" : "Kaydet ve Yayınla"}
            </button>
            {editing && <button onClick={() => { setEditing(null); setForm({ unit: "", type: "ozet", title: "", body: "", quiz: [] }); }} className="rounded-xl border border-slate-300 px-4 py-2.5 font-semibold text-slate-600">İptal</button>}
          </div>
        </div>
      )}

      {/* Ders → Ünite ağacı */}
      <div className="space-y-4 no-print">
        {tree.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Ders bulunamadı.</div>}
        {tree.map(({ subject, unitNames }) => {
          const c = colorOf(subject.color);
          const open = expanded.has(subject.name);
          const examRows = unitNames.map((u) => ({ u, ex: progressMap[key(subject.name, u)]?.exam })).filter((r) => r.ex);
          const totNet = examRows.reduce((s, r) => s + (r.ex.net || 0), 0);
          return (
            <div key={subject.id} data-testid={`oe-subject-${subject.name}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <button data-testid={`oe-subject-toggle-${subject.name}`} onClick={() => toggleExpand(subject.name)} className={`flex w-full items-center gap-2 px-5 py-3.5 text-left transition ${c.bg}`}>
                {open ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronRight className="h-5 w-5 text-slate-500" />}
                <span className={`h-3 w-3 rounded-full ${c.dot}`} />
                <span className={`font-display text-lg font-bold ${c.text}`}>{subject.name}</span>
                <span className="ml-auto text-sm font-semibold text-slate-500">{unitNames.length} ünite</span>
              </button>

              {open && (
                <div className="space-y-3 p-4">
                  {unitNames.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Bu derste ünite yok.</p>}
                  {unitNames.map((u) => {
                    const pr = progressMap[key(subject.name, u)];
                    const st = STATUS[pr?.status || "beklemede"];
                    const cs = getContents(subject.name, u);
                    const fs = getFiles(subject.name, u);
                    return (
                      <div key={u} data-testid={`oe-unit-${subject.name}-${u}`} className={`rounded-xl border border-slate-200 border-l-4 ${st.bar} bg-white p-4`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-display text-base font-bold text-slate-900">{u}</span>
                            <span data-testid={`oe-status-badge-${subject.name}-${u}`} className={`rounded-full border px-2.5 py-0.5 text-xs font-bold ${st.badge}`}>{st.label}</span>
                          </div>
                          {isEditor && studentId && (
                            <div className="flex items-center gap-1.5">
                              {STATUS_STEPS.map((s) => (
                                <button key={s.key} data-testid={`oe-set-${s.key}-${subject.name}-${u}`} onClick={() => setStatus(subject.name, u, s.key)}
                                  className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition active:scale-95 ${(pr?.status || "beklemede") === s.key ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{s.label}</button>
                              ))}
                              <button data-testid={`oe-exam-btn-${subject.name}-${u}`} onClick={() => openExam(subject.name, u)} className="flex items-center gap-1 rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-rose-700 active:scale-95">
                                <ClipboardCheck className="h-3.5 w-3.5" /> Sınav
                              </button>
                            </div>
                          )}
                        </div>

                        {(cs.length > 0 || fs.length > 0) && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {cs.map((ct) => (
                              <div key={ct.id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                                <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">{TYPES[ct.type] || ct.type}</span>
                                <span className="max-w-[140px] truncate text-sm text-slate-700">{ct.title || ct.unit || "İçerik"}</span>
                                {isAdmin && !ct.published && <span className="text-[10px] font-bold text-slate-400">gizli</span>}
                                <button data-testid={`oe-view-${ct.id}`} onClick={() => setView(ct)} className="rounded p-1 text-slate-500 hover:text-indigo-600">{ct.quiz?.length ? <HelpCircle className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                                {isAdmin && <>
                                  <button data-testid={`oe-edit-${ct.id}`} onClick={() => startEdit(ct)} className="rounded p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                                  <button data-testid={`oe-toggle-${ct.id}`} onClick={() => togglePublish(ct)} className="rounded p-1 text-slate-400 hover:text-slate-700">{ct.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
                                  <button data-testid={`oe-delete-${ct.id}`} onClick={() => remove(ct.id)} className="rounded p-1 text-rose-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                </>}
                              </div>
                            ))}
                            {fs.map((f) => (
                              <div key={f.id} data-testid={`oe-file-${f.id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                                <FileText className="h-4 w-4 text-slate-400" />
                                <span className="max-w-[140px] truncate text-sm text-slate-700">{f.original_filename}</span>
                                <button data-testid={`oe-file-download-${f.id}`} onClick={() => downloadFile(f)} className="rounded p-1 text-slate-500 hover:text-indigo-600"><Download className="h-4 w-4" /></button>
                                {isAdmin && <button data-testid={`oe-file-delete-${f.id}`} onClick={() => removeFile(f.id)} className="rounded p-1 text-rose-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                              </div>
                            ))}
                          </div>
                        )}

                        {pr?.exam && (
                          <div data-testid={`oe-exam-result-${subject.name}-${u}`} className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                            <span className="font-semibold text-slate-700">Sınav:</span>
                            <span className="text-emerald-600">D {pr.exam.dogru}</span>
                            <span className="text-rose-600">Y {pr.exam.yanlis}</span>
                            <span className="text-slate-500">B {pr.exam.bos}</span>
                            <span className="font-bold text-indigo-600">Net {fmtNet(pr.exam.net)}</span>
                            {isEditor && studentId && <button data-testid={`oe-exam-clear-${subject.name}-${u}`} onClick={() => clearExam(subject.name, u)} className="ml-auto rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {examRows.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-white">
                      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2 text-sm font-bold text-slate-700"><ClipboardCheck className="h-4 w-4 text-rose-500" /> Sınav Sonuç Tablosu — {subject.name}</div>
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                          <th className="px-4 py-2">Ünite</th><th className="px-3 py-2 text-center">D</th><th className="px-3 py-2 text-center">Y</th><th className="px-3 py-2 text-center">B</th><th className="px-3 py-2 text-center">Net</th>
                        </tr></thead>
                        <tbody>
                          {examRows.map((r) => (
                            <tr key={r.u} className="border-t border-slate-100">
                              <td className="px-4 py-2 font-semibold text-slate-800">{r.u}</td>
                              <td className="px-3 py-2 text-center text-emerald-600">{r.ex.dogru}</td>
                              <td className="px-3 py-2 text-center text-rose-600">{r.ex.yanlis}</td>
                              <td className="px-3 py-2 text-center text-slate-500">{r.ex.bos}</td>
                              <td className="px-3 py-2 text-center font-bold text-indigo-600">{fmtNet(r.ex.net)}</td>
                            </tr>
                          ))}
                          <tr className="border-t border-slate-200 bg-slate-50 font-bold text-slate-800">
                            <td className="px-4 py-2">Toplam</td><td colSpan={3} /><td className="px-3 py-2 text-center text-indigo-700">{fmtNet(totNet)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hidden print:block">
        {allShownContents.map((c) => (
          <div key={c.id} className="mb-6 break-inside-avoid">
            <h2 className="font-display text-xl font-extrabold">{c.subject_name} · {c.title || c.unit} <span className="text-sm font-normal text-slate-500">· {TYPES[c.type] || c.type}</span></h2>
            <Markdown text={c.body} />
          </div>
        ))}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{view?.title || view?.unit} <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{view && (TYPES[view.type] || view.type)}</span></DialogTitle></DialogHeader>
          {view && (view.quiz?.length ? <QuizPlayer quiz={view.quiz} onFinish={recordAttempt} /> : <Markdown text={view.body} />)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!examCtx} onOpenChange={(o) => !o && setExamCtx(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Ünite Sınavı</DialogTitle></DialogHeader>
          {examCtx && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">{examCtx.subject} · {examCtx.unit}</p>
              <div className="grid grid-cols-3 gap-3">
                <div><label className={lbl}>Doğru</label><input data-testid="oe-exam-dogru" type="number" min={0} value={examForm.dogru} onChange={(e) => setExamForm({ ...examForm, dogru: e.target.value })} className={`${inp} w-full`} /></div>
                <div><label className={lbl}>Yanlış</label><input data-testid="oe-exam-yanlis" type="number" min={0} value={examForm.yanlis} onChange={(e) => setExamForm({ ...examForm, yanlis: e.target.value })} className={`${inp} w-full`} /></div>
                <div><label className={lbl}>Boş</label><input data-testid="oe-exam-bos" type="number" min={0} value={examForm.bos} onChange={(e) => setExamForm({ ...examForm, bos: e.target.value })} className={`${inp} w-full`} /></div>
              </div>
              <div><label className={lbl}>Tarih</label><input data-testid="oe-exam-date" type="date" value={examForm.date} onChange={(e) => setExamForm({ ...examForm, date: e.target.value })} className={`${inp} w-full`} /></div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-700">Net (LGS): <b>{fmtNet((Number(examForm.dogru) || 0) - (Number(examForm.yanlis) || 0) / 3)}</b> · Kayıtta durum otomatik "Ezber Geçti" olur.</div>
              <button data-testid="oe-exam-save-btn" onClick={saveExam} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Kaydet</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuizPlayer({ quiz, onFinish }) {
  const valid = (quiz || []).filter((q) => q && q.question && Array.isArray(q.options) && q.options.length);
  const [ans, setAns] = useState({});
  const [done, setDone] = useState(false);
  const letter = (i) => String.fromCharCode(65 + i);
  if (!valid.length) return <p className="py-6 text-center text-slate-400">Bu test görüntülenemiyor.</p>;
  const correct = valid.filter((q, i) => ans[i] === q.answer).length;
  const wrong = valid.filter((q, i) => ans[i] && ans[i] !== q.answer).length;
  const empty = valid.length - correct - wrong;
  const net = (correct - wrong / 3).toFixed(2);
  return (
    <div className="space-y-5">
      {valid.map((q, i) => (
        <div key={i} data-testid={`quiz-q-${i}`} className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 font-semibold text-slate-800">{i + 1}. {q.question}</div>
          <div className="space-y-1.5">
            {q.options.map((o, oi) => {
              const L = letter(oi);
              const chosen = ans[i] === L;
              const isCorrect = done && q.answer === L;
              const isWrong = done && chosen && q.answer !== L;
              return (
                <button key={oi} data-testid={`quiz-opt-${i}-${L}`} disabled={done} onClick={() => setAns((a) => ({ ...a, [i]: L }))}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${isCorrect ? "border-emerald-400 bg-emerald-50 font-semibold text-emerald-800" : isWrong ? "border-rose-400 bg-rose-50 text-rose-800" : chosen ? "border-indigo-400 bg-indigo-50 text-indigo-800" : "border-slate-200 hover:border-slate-300"}`}>
                  {o}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {!done ? (
        <button data-testid="quiz-submit-btn" onClick={() => { setDone(true); onFinish && onFinish({ total: valid.length, dogru: correct, yanlis: wrong, bos: empty }); }} className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-700 active:scale-95">Testi Bitir</button>
      ) : (
        <div data-testid="quiz-result" className="rounded-2xl bg-gradient-to-br from-indigo-600 to-sky-500 p-5 text-center text-white">
          <div className="text-sm font-semibold uppercase tracking-wide text-white/80">Sonuç</div>
          <div className="my-1 font-display text-4xl font-extrabold">{net} Net</div>
          <div className="flex justify-center gap-4 text-sm">
            <span className="rounded-full bg-white/15 px-3 py-1">✓ {correct} Doğru</span>
            <span className="rounded-full bg-white/15 px-3 py-1">✗ {wrong} Yanlış</span>
            <span className="rounded-full bg-white/15 px-3 py-1">○ {empty} Boş</span>
          </div>
          <button data-testid="quiz-retry-btn" onClick={() => { setAns({}); setDone(false); }} className="mt-4 rounded-xl bg-white/20 px-5 py-2 font-semibold transition hover:bg-white/30">Tekrar Çöz</button>
        </div>
      )}
    </div>
  );
}

const lbl = "mb-1 block text-sm font-semibold text-slate-700";
const inp = "rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100";
